import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { MoreHorizontal, X } from "lucide-react";

const cn = (...classes) => classes.filter(Boolean).join(' ');

const MobileBottomNav = ({ navItems = [], maxVisibleItems = 4, className }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [safeAreaBottom, setSafeAreaBottom] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const setupSafeArea = async () => {
      if (!Capacitor.isNativePlatform()) return;

      try {
        const { SafeArea } = await import('@capacitor-community/safe-area');
        const result = await SafeArea.getSafeAreaInsets();
        setSafeAreaBottom(result.insets.bottom);
      } catch (error) {
        const platform = Capacitor.getPlatform();
        if (platform === 'ios') {
          setSafeAreaBottom(34);
        } else if (platform === 'android') {
          setSafeAreaBottom(0);
        }
      }
    };

    setupSafeArea();
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsExpanded(false);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Use navItems as provided by parent - no translation needed
  const visibleItems = navItems.slice(0, maxVisibleItems - 1);
  const hiddenItems = navItems.slice(maxVisibleItems - 1);
  const hasHiddenItems = hiddenItems.length > 0;

  return (
    <>
      {/* Backdrop for expanded state */}
      {isExpanded && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsExpanded(false)}
        />
      )}

      {/* Main Navigation Bar */}
      <div 
        className={cn(
          "fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50",
          className
        )}
        style={{ 
          paddingBottom: safeAreaBottom > 0 ? `${safeAreaBottom}px` : 'env(safe-area-inset-bottom)'
        }}
      >
        {/* Collapsed State */}
        {!isExpanded && (
          <div className="flex justify-around items-center h-16">
            {/* Visible nav items */}
            {visibleItems.map((item, index) => {
              const isActive = location.pathname === item.path || 
                (item.path !== '/' && location.pathname.startsWith(item.path));
              const Icon = item.icon;
              return (
                <button
                  key={`${item.path}-${index}`}
                  onClick={() => handleNavigation(item.path)}
                  className={cn(
                    "flex flex-col items-center justify-center w-full h-full active:bg-gray-100 transition-colors",
                    isActive ? "text-[#253985]" : "text-gray-500"
                  )}
                >
                  <div className="relative">
                    <Icon size={20} />
                    {item.badge && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <span className="text-xs mt-1 font-medium">{item.label}</span>
                </button>
              );
            })}

            {/* More button (only if there are hidden items) */}
            {hasHiddenItems && (
              <button
                onClick={toggleExpanded}
                className="flex flex-col items-center justify-center w-full h-full text-gray-500 active:bg-gray-100 transition-colors"
              >
                <MoreHorizontal size={20} />
                <span className="text-xs mt-1 font-medium">More</span>
              </button>
            )}
          </div>
        )}

        {/* Expanded State - Two Column Grid */}
        {isExpanded && (
          <div className="bg-white animate-in slide-in-from-bottom duration-300">
            {/* Close button row */}
            <div className="flex justify-end items-center px-4 py-2 border-b border-gray-100">
              <button
                onClick={() => setIsExpanded(false)}
                className="p-2 text-gray-500 hover:text-gray-700 active:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Two column grid for all items */}
            <div className="grid grid-cols-2 gap-2 p-4">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavigation(item.path)}
                    className={cn(
                      "flex flex-col items-center justify-center py-4 px-2 rounded-lg transition-colors active:scale-95",
                      isActive 
                        ? "text-[#253985] bg-blue-50 border border-blue-200" 
                        : "text-gray-500 hover:bg-gray-50 border border-transparent"
                    )}
                  >
                    <div className="relative mb-2">
                      <Icon size={24} />
                      {item.badge && (
                        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-center font-medium">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MobileBottomNav;